import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FileInputComponent } from '@catch/file-input';
import {
  ButtonComponent,
  InlineInfoComponent,
  ModalComponent,
  SalaryInputComponent,
} from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { fileTypes } from '../../../../../constants/filetypes';
import { DateInputComponent } from '../../../../../components/snippets/date-input/date-input.component';
import { SkillTestComponent } from '../../../../../components/snippets/skill-test/skill-test.component';
import {
  PlatformApplication,
  PlatformProfileSkill,
} from '../../../../models/platform.models';
import { PlatformMissingDataType } from '../../../../enums/platform-missing-data-type.enum';
import { PlatformJobsService } from '../../../../services/platform-jobs.service';
import { PlatformProfileSkillsComponent } from '../../../profile/components/profile-skills/platform-profile-skills.component';

@Component({
  selector: 'app-platform-jobs-complete-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    FileInputComponent,
    InlineInfoComponent,
    ModalComponent,
    SalaryInputComponent,
    TranslocoPipe,
    DateInputComponent,
    SkillTestComponent,
    PlatformProfileSkillsComponent,
  ],
  templateUrl: './platform-jobs-complete-modal.component.html',
  styleUrl: './platform-jobs-complete-modal.component.scss',
})
export class PlatformJobsCompleteModalComponent {
  private readonly fb = inject(FormBuilder);
  private readonly toastsService = inject(ToastsService);
  readonly jobs = inject(PlatformJobsService);

  readonly PlatformMissingDataType = PlatformMissingDataType;
  readonly fileTypes = fileTypes;
  readonly submitAttempt = signal(false);
  readonly formNonce = signal(0);

  readonly application = input<PlatformApplication | null>(null);
  readonly completed = output<void>();

  readonly missing = computed(() => this.application()?.missingData ?? []);

  readonly largeModal = computed(() =>
    this.missing().includes(PlatformMissingDataType.ISA)
  );

  readonly form: FormGroup = this.fb.group({
    salary: this.fb.control<number | null>(null),
    startDate: this.fb.control<number | null>(null),
    skills: this.fb.control<PlatformProfileSkill[]>([]),
    isa: this.fb.control<number[] | null>(null),
    cv: this.fb.control<File | null>(null),
    certificates: this.fb.control<File[] | File | null>(null),
  });

  private readonly modal = viewChild.required<ModalComponent>('modal');

  /**
   * Opens the follow-up form for the given application.
   */
  open(): void {
    this.submitAttempt.set(false);
    this.form.reset({
      salary: null,
      startDate: null,
      skills: [],
      isa: null,
      cv: null,
      certificates: null,
    });
    this.applyValidators();
    this.formNonce.update((nonce) => nonce + 1);
    this.modal().open();
  }

  /**
   * Closes the follow-up modal without saving.
   */
  close(): void {
    this.modal().close();
  }

  /**
   * Whether a missing-data type is requested for this application.
   * @param type - Missing data type
   * @returns True when the type is listed
   */
  isMissing(type: PlatformMissingDataType): boolean {
    return this.missing().includes(type);
  }

  /**
   * Salary control for the shared salary input.
   * @returns Salary form control
   */
  salaryControl(): UntypedFormControl {
    return this.form.controls['salary'] as UntypedFormControl;
  }

  /**
   * Start-date control for the shared date input.
   * @returns Start-date form control
   */
  startDateControl(): FormControl<number> {
    return this.form.controls['startDate'] as FormControl<number>;
  }

  /**
   * ISA control for the personality questionnaire.
   * @returns ISA form control
   */
  isaControl(): FormControl {
    return this.form.controls['isa'] as FormControl;
  }

  /**
   * CV file control for the shared file input.
   * @returns CV form control
   */
  cvControl(): UntypedFormControl {
    return this.form.controls['cv'] as UntypedFormControl;
  }

  /**
   * Certificates file control for the shared file input.
   * @returns Certificates form control
   */
  certificatesControl(): UntypedFormControl {
    return this.form.controls['certificates'] as UntypedFormControl;
  }

  /**
   * Shows a toast when a duplicate skill is added.
   */
  onDuplicateSkill(): void {
    this.toastsService.warning('jobboard.platform.profile.duplicate-skill');
  }

  /**
   * Stores follow-up data on the master profile and closes the modal.
   */
  submit(): void {
    this.submitAttempt.set(true);
    if (this.form.invalid) {
      return;
    }
    const application = this.application();
    if (!application) {
      return;
    }
    this.jobs.completeFollowUp(
      {
        salary: this.isMissing(PlatformMissingDataType.SALARY)
          ? this.form.controls['salary'].value
          : undefined,
        startDate: this.isMissing(PlatformMissingDataType.START_DATE)
          ? this.form.controls['startDate'].value
          : undefined,
        skills: this.isMissing(PlatformMissingDataType.SKILLS)
          ? (this.form.controls['skills'].value as PlatformProfileSkill[])
          : undefined,
        isaCompleted: this.isMissing(PlatformMissingDataType.ISA)
          ? true
          : undefined,
        isaAnswers: this.isMissing(PlatformMissingDataType.ISA)
          ? (this.form.controls['isa'].value as number[] | null) ?? undefined
          : undefined,
        cvFile: this.isMissing(PlatformMissingDataType.DOCUMENTS)
          ? this.asFile(this.form.controls['cv'].value)
          : undefined,
        certificateFiles: this.isMissing(PlatformMissingDataType.DOCUMENTS)
          ? this.asFiles(this.form.controls['certificates'].value)
          : undefined,
      },
      application.id
    );
    this.completed.emit();
    this.close();
  }

  /**
   * Applies required validators for the currently missing data types.
   */
  private applyValidators(): void {
    this.toggleRequired('salary', this.isMissing(PlatformMissingDataType.SALARY));
    this.toggleRequired(
      'startDate',
      this.isMissing(PlatformMissingDataType.START_DATE)
    );
    this.toggleRequired('isa', this.isMissing(PlatformMissingDataType.ISA));
    const skillsControl = this.form.controls['skills'];
    skillsControl.clearValidators();
    if (this.isMissing(PlatformMissingDataType.SKILLS)) {
      skillsControl.addValidators(this.skillsRequiredValidator());
    }
    skillsControl.updateValueAndValidity();
    this.form.setValidators(
      this.isMissing(PlatformMissingDataType.DOCUMENTS)
        ? this.documentsValidator()
        : null
    );
    this.form.updateValueAndValidity();
  }

  /**
   * Adds or removes the required validator on a named control.
   * @param name - Control name
   * @param required - Whether the field is required
   */
  private toggleRequired(name: string, required: boolean): void {
    const control = this.form.controls[name];
    control.clearValidators();
    if (required) {
      control.addValidators(Validators.required);
    }
    control.updateValueAndValidity();
  }

  /**
   * Requires at least one skill when skills are missing.
   * @returns Validator
   */
  private skillsRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const skills = control.value as PlatformProfileSkill[] | null;
      return skills && skills.length > 0 ? null : { required: true };
    };
  }

  /**
   * Requires at least one document when documents are missing.
   * @returns Group validator
   */
  private documentsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.isMissing(PlatformMissingDataType.DOCUMENTS)) {
        return null;
      }
      const cv = control.get('cv')?.value;
      const certificates = control.get('certificates')?.value;
      const hasCertificates = Array.isArray(certificates)
        ? certificates.length > 0
        : Boolean(certificates);
      return cv || hasCertificates ? null : { documents: true };
    };
  }

  /**
   * Returns a single file from a file-input value.
   * @param value - Control value
   * @returns File or null
   */
  private asFile(value: unknown): File | null {
    if (value instanceof File) {
      return value;
    }
    if (Array.isArray(value) && value[0] instanceof File) {
      return value[0];
    }
    return null;
  }

  /**
   * Returns files from a file-input value.
   * @param value - Control value
   * @returns Files
   */
  private asFiles(value: unknown): File[] {
    if (Array.isArray(value)) {
      return value.filter((file): file is File => file instanceof File);
    }
    return value instanceof File ? [value] : [];
  }
}
